import { Anchor, ArrowDownToLine, ArrowUpFromLine, Flag } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Container, PillButton, SectionHeading } from "@/components/common/ui";
import { LazyMount } from "@/components/common/LazyMount";
import { BaseExplorer } from "@/components/bases/BaseExplorer";
import { PcsChecklist } from "@/components/military/PcsChecklist";
import { VaLoanFacts } from "@/components/military/VaLoanFacts";
import { MortgageCalculator } from "@/components/calculators/MortgageCalculator";
import { PcsCta } from "@/components/home/Sections";
import { StepList } from "@/components/common/StepList";
import { IMAGES } from "@/lib/site";
import { TESTIMONIALS } from "@/lib/testimonials";

const PILLARS = [
  { id: "in", Icon: ArrowDownToLine, title: "PCS to Hampton Roads", body: "Shortlist homes by distance to your installation and make the most of your house-hunting leave." },
  { id: "out", Icon: ArrowUpFromLine, title: "PCS away", body: "Sell for 4.75% total commission, or keep it as a rental and let us handle property management." },
  { id: "stay", Icon: Flag, title: "Retiring or separating here", body: "Put your V.A. loan benefit to work on a forever home in Hampton Roads or Greater Richmond." },
];

const MOVE_IN = [
  { title: "Send us your orders details", body: "Your installation, report date and family must-haves. We set your search radius around your base on our 3D map." },
  { title: "Get pre-approved", body: "We help guide you to local lenders who know the V.A. loan program, the Virginia Housing Grant and FHA 203K." },
  { title: "Shortlist before you arrive", body: "Compare homes by straight-line distance and live driving routes to your installation, then narrow it down together." },
  { title: "Pack your house-hunting days", body: "We set up appointments to get you into as many homes as possible while you're in town." },
  { title: "Offer, inspection, closing", body: "Our buyer agents have a simple philosophy: “Always offer less and ask for more!” We stay with you through closing — and after." },
];

const MOVE_OUT = [
  { title: "Price it right", body: "A free comparative market analysis using recent neighborhood sales." },
  { title: "List for 4.75% total", body: "NEVER PAY 6% AGAIN. Our Seller's Advantage Discount Listing Program saves thousands." },
  { title: "We Sell — You Vacation", body: "We conduct the open house, prepare your home and take multiple offers while you're away — or already at your next duty station." },
  { title: "Or keep it as a rental", body: "Handling the property management and rental of your current home is part of what we do." },
];

const MILITARY_REVIEW = TESTIMONIALS.find((t) => t.id === "t-cordle");

export default function MilitaryPage() {
  return (
    <div data-testid="military-page">
      <section className="relative flex h-[88svh] min-h-[600px] items-end overflow-hidden bg-neutral-900">
        <img src={IMAGES.homecoming} alt="A sailor's homecoming" className="absolute inset-0 h-full w-full object-cover opacity-75" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/40" />
        <Container className="relative z-10 pb-16 sm:pb-20">
          <p className="eyebrow animate-rise-in !text-brass">Military relocation · Hampton Roads & Greater Richmond</p>
          <h1 className="mt-4 max-w-3xl animate-rise-in font-display text-5xl font-normal leading-[1.03] text-white [animation-delay:120ms] sm:text-6xl lg:text-7xl">
            You've served. <br />We'll handle the move.
          </h1>
          <p className="mt-5 max-w-2xl animate-rise-in text-lg text-white/85 [animation-delay:220ms]">
            We are proud to represent the treasured men and women of our armed forces who often must deal with the stress that comes with long distance relocations.
          </p>
          <div className="mt-8 flex animate-rise-in flex-wrap gap-3 [animation-delay:320ms]">
            <PillButton href="#pcs-form" tone="light" testId="military-hero-pcs">Start my PCS plan</PillButton>
            <PillButton to="/bases" tone="ghostLight" arrow={false} testId="military-hero-bases">Explore bases in 3D</PillButton>
          </div>
        </Container>
      </section>

      <section className="border-b border-neutral-200 py-14">
        <Container className="grid gap-10 lg:grid-cols-[1fr_2fr] lg:items-center">
          <div className="flex items-start gap-4">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-ink text-brass"><Anchor className="h-6 w-6" /></span>
            <p className="font-display text-2xl leading-snug text-ink">Our Broker in charge is a 30-year retired Navy Senior Chief.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            {PILLARS.map(({ id, Icon, title, body }) => (
              <div key={id} data-testid={`military-pillar-${id}`}>
                <Icon className="h-5 w-5 text-brass-deep" />
                <p className="mt-3 text-lg font-medium">{title}</p>
                <p className="mt-1 text-[15px] text-neutral-600">{body}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 sm:py-20" data-testid="military-explorer-section">
        <Container><SectionHeading eyebrow="Installation explorer" title="See your next duty station in 3D" subtitle="Fly in, orbit the base and see every home around it — before you ever take leave." /></Container>
        <div className="mx-auto mt-8 max-w-[1440px] sm:px-8">
          <LazyMount className="h-[640px] overflow-hidden sm:rounded-md lg:h-[720px]"><BaseExplorer className="h-full" testId="military-base-explorer" /></LazyMount>
        </div>
      </section>

      <section className="bg-mist py-16 sm:py-20">
        <Container>
          <SectionHeading title="Moving in or moving out, we've done this before" />
          <Tabs defaultValue="in" className="mt-8">
            <TabsList className="h-11 rounded-full bg-white p-1">
              <TabsTrigger value="in" className="rounded-full px-5 data-[state=active]:bg-ink data-[state=active]:text-white" data-testid="move-tab-in">PCS in</TabsTrigger>
              <TabsTrigger value="out" className="rounded-full px-5 data-[state=active]:bg-ink data-[state=active]:text-white" data-testid="move-tab-out">PCS out</TabsTrigger>
            </TabsList>
            <TabsContent value="in" className="mt-10"><StepList steps={MOVE_IN} testId="move-in-steps" /></TabsContent>
            <TabsContent value="out" className="mt-10">
              <StepList steps={MOVE_OUT} testId="move-out-steps" />
              <PillButton to="/sellers" className="mt-8" testId="move-out-sellers">See the seller program</PillButton>
            </TabsContent>
          </Tabs>
        </Container>
      </section>

      <section id="pcs-checklist" className="scroll-mt-20 py-16 sm:py-20">
        <Container>
          <SectionHeading eyebrow="Your PCS checklist" title="Check it off as you go" subtitle="Saved on this device, so you can come back to it any time before report day." />
          <div className="mt-8"><PcsChecklist /></div>
        </Container>
      </section>

      <section id="va-loans" className="scroll-mt-20 border-t border-neutral-200 py-16 sm:py-20">
        <Container className="grid gap-12 lg:grid-cols-[1fr_1.3fr]">
          <div>
            <SectionHeading eyebrow="V.A. loans" title="Your benefit, explained simply" subtitle="The basics most families ask us about. Your lender confirms the details for your situation." />
            <div className="mt-6"><VaLoanFacts /></div>
          </div>
          <div>
            <p className="mb-4 text-lg font-medium">V.A. payment vs. your BAH</p>
            <MortgageCalculator defaultLoan="va" showBah testId="military-calculator" />
          </div>
        </Container>
      </section>

      {MILITARY_REVIEW && (
        <section className="bg-mist py-20">
          <Container className="max-w-[920px] text-center">
            <p className="eyebrow">From a military family</p>
            <blockquote className="mt-6 font-display text-2xl italic leading-relaxed sm:text-[28px]">“{MILITARY_REVIEW.text}”</blockquote>
            <p className="mt-6 text-[15px] font-medium">{MILITARY_REVIEW.name}</p>
          </Container>
        </section>
      )}

      <PcsCta />
    </div>
  );
}
